import { Link } from "wouter";
import PageMeta from "../components/PageMeta";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Download, ExternalLink, Mail, FileText, Image, Users } from "lucide-react";
import { useEffect } from "react";
import Lenis from "lenis";

const FOUNDERS = [
  {
    name: "Christopher",
    role: "CEO & Co-founder",
    bio: "Christopher is a veterinary entrepreneur and the founder of Baby Bear Cattery and Wild Splendour Savannahs — two of Singapore's most respected exotic cat breeders. Drawing on 15 years of experience in Singapore's pet community, he co-founded Furbaby to build the clinic he always wished existed: one that knows your pet as well as you do. He holds a background in biological sciences and has served on the advisory board of the Singapore Cat Club.",
  },
  {
    name: "Margot",
    role: "Chief Experience Officer & Co-founder",
    bio: "Margot is a hospitality and experience designer who has led creative direction for luxury hotels and wellness brands across Asia and Europe. She joined Furbaby to apply the principles of world-class service design to veterinary care — believing that how a clinic feels is inseparable from how well it heals. She is also a visiting lecturer on experience design at NUS and NTU.",
  },
];

const FACTS = [
  { label: "Founded", value: "2024" },
  { label: "Seed raise", value: "S$2 million" },
  { label: "Location", value: "Singapore" },
  { label: "Opening", value: "2026" },
  { label: "Founding member target", value: "2,000 members" },
  { label: "Max loyalty discount", value: "90% off hospital bills" },
];

const ASSETS = [
  { icon: Image, label: "Founder photographs", desc: "High-resolution headshots of Christopher and Margot (PNG, 300dpi)", href: "#" },
  { icon: Image, label: "Clinic render", desc: "Architectural render of the Furbaby clinic interior (PNG, print-ready)", href: "#" },
  { icon: FileText, label: "Company overview", desc: "One-page PDF — who we are, what we do, why Singapore", href: "#" },
  { icon: FileText, label: "FurGenomics explainer", desc: "One-page PDF — our proprietary genomics panel explained for a general audience", href: "#" },
  { icon: Image, label: "Brand assets", desc: "Logo files, colour palette, usage guidelines (ZIP)", href: "#" },
];

const COVERAGE = [
  { outlet: "Target outlets", list: ["Channel NewsAsia (CNA)", "The Business Times", "Tatler Singapore", "Vogue Singapore", "The Straits Times Life"] },
  { outlet: "Broadcast", list: ["CNA938", "Money FM 89.3"] },
  { outlet: "Trade / veterinary", list: ["Vets Today", "VetSurgeon.org"] },
];

export default function Press() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.02, smoothWheel: true });
    let rafId: number;
    function raf(t: number) { lenis.raf(t); rafId = requestAnimationFrame(raf); }
    rafId = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); };
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden cursor-default selection:bg-primary/40">
      <PageMeta
        title="Press — Furbaby Vet Care"
        description="Press kit, media contacts and company information for Furbaby — Singapore's first premium veterinary membership clinic."
        canonical="https://furbaby.vet/main/press"
        ogTitle="Furbaby Press Kit"
        ogDescription="Press resources, founder bios, clinic renders and company facts for media covering Furbaby Vet Care."
      />

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 md:px-12 py-6 flex justify-between items-center">
        <Link href="/" className="group flex items-center gap-2 font-grotesk text-xs uppercase tracking-widest hover:opacity-60 transition-opacity duration-500">
          <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform duration-500" />
          Home
        </Link>
        <span className="font-serif text-2xl font-bold italic tracking-tighter">Furbaby</span>
        <a
          href="mailto:press@furbaby.vet"
          className="group flex items-center gap-2 font-grotesk text-xs uppercase tracking-widest hover:opacity-60 transition-opacity duration-500"
        >
          Media Enquiries
          <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-500" />
        </a>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-48 md:pb-24 px-4 md:px-20">
        <motion.div
          className="max-w-5xl"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-primary mb-8">Press & Media</p>
          <h1 className="font-serif text-[10vw] md:text-[6vw] leading-[0.9] tracking-tighter mb-10">
            The story of<br />
            <span className="italic text-primary">Furbaby.</span>
          </h1>
          <p className="font-sans text-xl md:text-2xl opacity-65 max-w-2xl leading-relaxed">
            Singapore's first premium veterinary membership clinic. Care that compounds. A loyalty model that gets better every year.
          </p>
        </motion.div>
      </section>

      {/* Boilerplate */}
      <section className="py-16 md:py-24 px-4 md:px-20 bg-muted/20 border-y border-border/30">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-primary mb-6">About Furbaby</p>
            <p className="font-sans text-lg leading-relaxed opacity-75 max-w-3xl mb-6">
              Furbaby is Singapore's first premium veterinary membership clinic — a model that replaces traditional pet insurance with something better: a membership that provides unlimited consultations, preventive care, and genomics-informed wellness planning, with a loyalty discount on hospital bills that grows every year you stay.
            </p>
            <p className="font-sans text-base leading-relaxed opacity-60 max-w-3xl mb-6">
              The Furbaby loyalty schedule gives founding members — the first 2,000 to join — a 10% hospital discount increase each year, reaching 90% off by Year 9. The longer you're a member, the less a sudden surgery, emergency consultation, or end-of-life procedure costs. There are no claims, no age limits, no breed exclusions. Just care that deepens.
            </p>
            <p className="font-sans text-base leading-relaxed opacity-60 max-w-3xl">
              At the centre of Furbaby's clinical offering is FurGenomics — a proprietary genomic screening panel integrated into every membership tier. From breed-specific disease risk to nutritional variants, genomic data informs every care plan from the first consultation. Furbaby is founded by Christopher and Margot, and backed by a S$2 million seed round.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Facts */}
      <section className="py-16 md:py-24 px-4 md:px-20">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-primary mb-10">Key Facts</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-border/30">
              {FACTS.map((f) => (
                <div key={f.label} className="bg-background p-8">
                  <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-50 mb-3">{f.label}</p>
                  <p className="font-serif text-2xl md:text-3xl">{f.value}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Founders */}
      <section className="py-16 md:py-24 px-4 md:px-20 bg-muted/20 border-y border-border/30">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center gap-3 mb-10">
              <Users className="w-4 h-4 text-primary opacity-70" strokeWidth={1.5} />
              <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-primary">Founders</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {FOUNDERS.map((f, i) => (
                <motion.div
                  key={f.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                >
                  <div className="w-full aspect-[4/3] bg-muted/30 border border-border/40 rounded-2xl mb-6 flex items-center justify-center">
                    <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-30">Photo — {f.name}</p>
                  </div>
                  <h3 className="font-serif text-3xl mb-1">{f.name}</h3>
                  <p className="font-grotesk text-[10px] uppercase tracking-widest text-primary mb-5">{f.role}</p>
                  <p className="font-sans text-sm opacity-65 leading-relaxed">{f.bio}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Press Assets */}
      <section className="py-16 md:py-24 px-4 md:px-20">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-primary mb-10">Press Kit Assets</p>
            <div className="space-y-3">
              {ASSETS.map((a, i) => (
                <motion.div
                  key={a.label}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                  className="group flex items-center gap-6 p-6 border border-border/50 hover:border-primary/30 bg-background transition-colors duration-400"
                >
                  <div className="w-10 h-10 flex items-center justify-center border border-border/40 shrink-0">
                    <a.icon className="w-4 h-4 text-primary opacity-60" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-serif text-xl mb-1">{a.label}</h4>
                    <p className="font-sans text-xs opacity-55">{a.desc}</p>
                  </div>
                  <a
                    href={`mailto:press@furbaby.vet?subject=Press%20Kit%20Request%20—%20${encodeURIComponent(a.label)}`}
                    className="flex items-center gap-2 font-grotesk text-[10px] uppercase tracking-widest opacity-50 group-hover:opacity-100 group-hover:text-primary transition-all duration-400"
                  >
                    <Mail className="w-3 h-3" />
                    Request
                  </a>
                </motion.div>
              ))}
            </div>
            <p className="font-sans text-xs opacity-40 mt-6 leading-relaxed">
              All press assets are available upon request. Please email{" "}
              <a href="mailto:press@furbaby.vet" className="underline opacity-70 hover:opacity-100 transition-opacity">press@furbaby.vet</a>{" "}
              with your publication, deadline, and the assets required.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Coverage targets */}
      <section className="py-16 md:py-24 px-4 md:px-20 bg-muted/20 border-y border-border/30">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-primary mb-10">Publication Focus</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {COVERAGE.map((c) => (
                <div key={c.outlet}>
                  <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-50 mb-5">{c.outlet}</p>
                  <div className="space-y-3">
                    {c.list.map((outlet) => (
                      <p key={outlet} className="font-serif text-lg">{outlet}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-24 md:py-40 px-4 md:px-20">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-primary mb-8">Media Contact</p>
            <h2 className="font-serif text-5xl md:text-7xl tracking-tighter leading-none mb-10">
              Let's talk<br />
              <span className="italic text-primary">to your audience.</span>
            </h2>
            <p className="font-sans text-lg opacity-60 max-w-xl mx-auto mb-10 leading-relaxed">
              For press enquiries, interview requests, or to arrange access to the clinic, please contact our team directly.
            </p>
            <a
              href="mailto:press@furbaby.vet"
              className="inline-flex items-center gap-3 font-grotesk text-xs uppercase tracking-widest border border-foreground/20 px-8 py-4 hover:bg-foreground hover:text-background transition-all duration-500"
            >
              <Mail className="w-3.5 h-3.5" />
              press@furbaby.vet
              <ExternalLink className="w-3 h-3 opacity-40" />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
